import React, { useState, useContext, useEffect } from 'react';

import moment from 'moment';

/** context */
import { UserContext } from '../../context/UserContext';
import { formatUSDCurrency } from '../../helpers/format';

/** service */
import {
  updateApplication,
  validateProperty,
  getApprovedAmazonLoanDetails,
} from '../../service/api';

export default function PaymentPlanReview({ className, gotoNextStep, goBack }) {
  const { appData, profile, updateAppData, setLoading } = useContext(UserContext);
  const multiApprovedRateFeature = appData?.applicationFeatures?.find(
    (feature) => feature.itemType === 'MultiApprovedRateProperty'
  );
  const autopayFeature = appData?.applicationFeatures.find((feature) => feature.name === 'autopay');

  const [autopay, setAutopay] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchApprovedAmazonLoanDetails() {
      try {
        const res = await getApprovedAmazonLoanDetails();
      } catch (err) {
        goBack();
      }
    }
    fetchApprovedAmazonLoanDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchAutopay() {
      if (!autopayFeature.value || autopayFeature.value?.length === 0) {
        return;
      }
      if (autopayFeature.value[0]?.consented) {
        setAutopay('yes');
      } else if (autopayFeature.value[0]?.consented === false) {
        setAutopay('no');
      }
    }
    fetchAutopay();
  }, [autopayFeature]);

  const selectedPlan =
    multiApprovedRateFeature?.value?.rates[multiApprovedRateFeature?.value?.selectedIndex];

  const scheduledItems = selectedPlan?.amortizationScheduleList
    ? selectedPlan?.amortizationScheduleList.slice(1)
    : [];

  const handleSubmit = async () => {
    gotoNextStep(autopay);
    setLoading(true);
    try {
      const autopayFeature = appData?.applicationFeatures.find(
        (feature) => feature.name === 'autopay'
      );

      const updatedData = await updateApplication([
        {
          itemType: autopayFeature.itemType,
          dataType: autopayFeature.dataType,
          name: autopayFeature.name,
          value: [
            {
              userId: profile.id,
              consented: autopay === 'yes',
            },
          ],
        },
      ]);

      if (updatedData.data) {
        updateAppData(updatedData.data);
      }

      const validationData = await validateProperty(autopayFeature.name);

      if (validationData.data && validationData.data.errors) {
        if (validationData.data.errors.length) {
          validationData.data.errors.map((error) => {
            const attributes = error.property.split('.');
          });
          return false;
        }
      }

      setLoading(false);
    } catch (error) {
      console.log(error);
      const errors = error.response.data.errors;
      if (errors.length) {
        setError(errors[0].message);
      }
      setLoading(false);
      return;
    }

    gotoNextStep(autopay);
  };

  return (
    <div className={className}>
      <div className={`${className}__title`}>
        <h1>Review your financing terms</h1>
      </div>
      <div className={`${className}__divider`} />
      <div className={`${className}__plan-data`}>
        $ {selectedPlan?.monthlyPayment} /mo for {selectedPlan?.approvedRate?.approvedTerm} months
      </div>
      <div className={`${className}__divider`} />
      <div className={`${className}__order-items`}>
        <div className={`${className}__order-items__item`}>
          <div className={`${className}__order-items__item-key`}>
            <span className="bullet">&#8226;</span>
            Total interest ({selectedPlan?.approvedRate?.approvedApr}% APR)
          </div>
          <div className={`${className}__order-items__item-value has-width`}>
            {formatUSDCurrency(selectedPlan?.totalInterest)}
          </div>
        </div>
        <div className={`${className}__order-items__item`}>
          <div className={`${className}__order-items__item-key`}>
            <span className="bullet">&#8226;</span>
            Total of payments
          </div>
          <div className={`${className}__order-items__item-value has-width`}>
            {formatUSDCurrency(selectedPlan?.totalPayment)}
          </div>
        </div>
        <div className={`${className}__order-items__item`}>
          <div className={`${className}__order-items__item-key`}>
            <span className="bullet">&#8226;</span>
            First Payment Date
          </div>
          <div className={`${className}__order-items__item-value has-width`}>
            {moment(scheduledItems[0]?.paymentDate).format('MM-DD-YYYY')}
          </div>
        </div>
      </div>
      <div className={`${className}__divider`} />

      <div className={`${className}__autopay-selector`}>
        <div className={`${className}__label`}>Do you want to enroll in Auto Pay?</div>
        <div className="custom-radio-button left bnpl">
          <label htmlFor="custom-radio-yes">
            <input
              type="radio"
              id="custom-radio-yes"
              name="autopay-checks"
              checked={autopay === 'yes'}
              onChange={() => setAutopay('yes')}
            />
            Yes, enroll me in Auto Pay
          </label>
        </div>
        <div className="custom-radio-button left bnpl">
          <label htmlFor="custom-radio-no">
            <input
              type="radio"
              id="custom-radio-no"
              name="autopay-checks"
              checked={autopay === 'no'}
              onChange={() => setAutopay('no')}
            />
            No, do not enroll me in Auto Pay
          </label>
        </div>
      </div>

      <div className={`${className}__divider`} />
      <div className={`${className}__autopay-descriptions`}>
        <div className={`${className}__autopay-descriptions__item`}>
          <span className="bullet">&#8226;</span>You can change your selection at any time
        </div>
        <div className={`${className}__autopay-descriptions__item`}>
          <span className="bullet">&#8226;</span>Repayment through Auto Pay is not a condition of
          financing
        </div>
      </div>

      {error && <div className={`${className}__error`}>{error}</div>}
      <div className={`${className}__button-container`} disabled={autopay == ''}>
        <button onClick={handleSubmit}>Proceed</button>
      </div>
    </div>
  );
}
